import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Input,
  Select,
} from 'antd';
import moment from 'moment';
import { captureAnalytics } from '../../../actions/preload';
import DatePicker from '../../../components/DatePicker';
import Button from '../../../components/Button';
import { assigneeOptionLabel } from '../CallFollowUps/callFollowUpFormUtils';

const optionItems = (items, labelBuilder = (item) => item.name) => (
  (items || []).map((item) => ({
    value: String(item.id),
    label: labelBuilder(item),
  }))
);

const lifecycleOptions = [
  { value: 'open', label: 'Open' },
  { value: 'all', label: 'All' },
];

const dueOptions = [
  { value: 'all', label: 'Any due date' },
  { value: 'overdue', label: 'Overdue' },
];

const sortOptions = [
  { value: 'created_at', label: 'Newest' },
  { value: 'due_date', label: 'Due date' },
  { value: 'overdue', label: 'Overdue first' },
];

const ownerFilterState = (value) => {
  if (!value) return 'all';
  return value === 'unassigned' ? 'unassigned' : 'assigned';
};

const FollowUpFilters = ({
  filters,
  options,
  optionsError,
  onChange,
  onClear,
  onAssigneeSearch,
}) => {
  const dateFormat = ['MM/DD/YYYY', 'MM-DD-YYYY', 'YYYY-MM-DD'];
  const [searchDraft, setSearchDraft] = useState(filters.search || '');
  const dueDateValue = filters.due_date ? moment(filters.due_date, dateFormat, true) : null;
  const commitSearch = (value) => {
    if (value !== (filters.search || '')) onChange({ search: value });
  };

  useEffect(() => {
    setSearchDraft(filters.search || '');
  }, [filters.search]);

  return (
    <>
      <div className="follow-up-filter-toolbar">
        <Input.Search
          data-testid="follow-up-search"
          placeholder="Search caller, DOB, or phone"
          className="follow-up-search-input"
          value={searchDraft}
          allowClear
          onChange={(event) => {
            setSearchDraft(event.target.value);
            if (event.target.value.length === 0) onChange({ search: '' });
          }}
          onBlur={(event) => commitSearch(event.target.value)}
          onSearch={commitSearch}
        />
        <Select
          className="voice-outbound-subheader-select"
          value={filters.lifecycle}
          options={lifecycleOptions}
          onChange={(value) => onChange({ lifecycle: value })}
          style={{ minWidth: 92 }}
        />
        <Select
          className="voice-outbound-subheader-select"
          placeholder="Owner"
          value={filters.assignee_id || undefined}
          allowClear
          showSearch
          filterOption={false}
          options={optionItems(options?.assignees, assigneeOptionLabel)}
          onChange={(value) => {
            captureAnalytics('call_followup_owner_filter_changed', { owner_state: ownerFilterState(value) });
            onChange({ assignee_id: value || null });
          }}
          onSearch={onAssigneeSearch}
          style={{ minWidth: 176 }}
          dropdownMatchSelectWidth={false}
        />
        <Select
          className="voice-outbound-subheader-select"
          placeholder="Status"
          value={filters.current_status_id || undefined}
          allowClear
          options={optionItems(options?.call_followup_statuses)}
          onChange={(value) => onChange({ current_status_id: value || null })}
          style={{ minWidth: 145 }}
          dropdownMatchSelectWidth={false}
        />
        <Select
          className="voice-outbound-subheader-select"
          value={filters.due_mode}
          options={dueOptions}
          onChange={(value) => onChange({ due_mode: value, due_date: null })}
          style={{ minWidth: 128 }}
        />
        <DatePicker
          className="follow-up-due-date-picker"
          value={dueDateValue?.isValid() ? dueDateValue : null}
          onChange={(date) => onChange({ due_date: date ? date.format('YYYY-MM-DD') : null, due_mode: 'all' })}
          format={dateFormat}
          placeholder="Due date"
        />
        <Select
          className="voice-outbound-subheader-select"
          placeholder="Final outcome"
          value={filters.final_outcome_id || undefined}
          allowClear
          options={optionItems(options?.call_dispositions)}
          onChange={(value) => onChange({ final_outcome_id: value || null })}
          style={{ minWidth: 156 }}
          dropdownMatchSelectWidth={false}
        />
        <div className="follow-up-filter-actions">
          <Select
            className="voice-outbound-subheader-select"
            value={filters.sort}
            options={sortOptions}
            onChange={(value) => onChange({ sort: value, sort_direction: value === 'due_date' ? 'asc' : 'desc' })}
            style={{ minWidth: 130 }}
          />
          <div className="clear-button">
            <Button type="primary" size="xxsmall" label="Clear" onClick={onClear} />
          </div>
        </div>
      </div>
      {optionsError && <div className="follow-up-filter-error">{optionsError}</div>}
    </>
  );
};

FollowUpFilters.defaultProps = {
  options: {},
  optionsError: null,
  onChange: () => {},
  onClear: () => {},
  onAssigneeSearch: () => {},
};

FollowUpFilters.propTypes = {
  filters: PropTypes.shape({
    search: PropTypes.string,
    lifecycle: PropTypes.string,
    assignee_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    current_status_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    due_mode: PropTypes.string,
    due_date: PropTypes.string,
    final_outcome_id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    sort: PropTypes.string,
    sort_direction: PropTypes.string,
  }).isRequired,
  options: PropTypes.object,
  optionsError: PropTypes.string,
  onChange: PropTypes.func,
  onClear: PropTypes.func,
  onAssigneeSearch: PropTypes.func,
};

export default FollowUpFilters;
